/**
 * SharedDataContext - Main Data Store
 * Centralized reactive data management with event notifications
 */

import { devError } from '../../../../../utils/devLog';
import { EventStream } from './EventStream';
import { 
    DataChangeEventType,
    createEvent 
} from '../types/EventTypes';
import {
    DataSubscriptionOptions,
    DataSubscription,
    DataSchema,
    DataNode,
    DataStatistics,
    BatchUpdateResult
} from '../types/DataTypes';
import { initializeDefaultSchemas } from './DataSchemas';
import { 
    normalizePath, 
    deepEqual, 
    pathMatches, 
    validateData, 
    transformData 
} from './DataUtils';

/**
 * Main shared data context class
 */
export class SharedDataContext {
    private static instance: SharedDataContext | null = null;
    
    private data: Map<string, DataNode> = new Map();
    private subscriptions: Map<string, DataSubscription> = new Map();
    private eventStream: EventStream;
    private subscriptionCounter = 0;
    private isDestroyed = false;

    private constructor() {
        this.eventStream = EventStream.getInstance();
        this.initializeDefaultData();
    }

    /**
     * Get singleton instance
     */
    public static getInstance(): SharedDataContext {
        if (!SharedDataContext.instance) {
            SharedDataContext.instance = new SharedDataContext();
        }
        return SharedDataContext.instance;
    }

    /**
     * Set data at a specific path
     */
    public set<T = any>(
        path: string, 
        value: T, 
        source: string = 'unknown',
        options: { 
            validate?: boolean;
            transform?: boolean;
            silent?: boolean;
        } = {}
    ): boolean {
        if (this.isDestroyed) {
            throw new Error('SharedDataContext has been destroyed');
        }

        const { validate = true, transform = true, silent = false } = options;
        const normalizedPath = normalizePath(path);
        const existingNode = this.data.get(normalizedPath);
        const oldValue = existingNode?.value;

        // Check readonly
        if (existingNode?.schema?.readonly) {
            return false;
        }

        let finalValue = value;

        // Apply transform if needed
        if (transform && existingNode?.schema?.transform) {
            finalValue = transformData(value, normalizedPath, existingNode.schema.transform);
        }

        // Validate if needed
        if (validate && existingNode?.schema?.validator) {
            if (!validateData(finalValue, normalizedPath, existingNode.schema.validator)) {
                return false;
            }
        }

        // Check if value actually changed
        if (deepEqual(oldValue, finalValue)) {
            return true; // No change needed
        }

        // Update data
        const newNode: DataNode = {
            value: finalValue,
            schema: existingNode?.schema,
            lastModified: Date.now(),
            modifiedBy: source,
            subscriptions: existingNode?.subscriptions || new Set()
        };

        this.data.set(normalizedPath, newNode);

        // Emit change event
        if (!silent) {
            this.emitDataChangeEvent(normalizedPath, oldValue, finalValue, 'update', source);
        }

        // Notify subscribers
        this.notifySubscribers(normalizedPath, finalValue, oldValue);

        return true;
    }

    /**
     * Get data at a specific path
     */
    public get<T = any>(path: string, defaultValue?: T): T {
        const normalizedPath = normalizePath(path);
        const node = this.data.get(normalizedPath);
        
        if (node === undefined) {
            return defaultValue as T;
        }

        return node.value as T;
    }

    /**
     * Check if path exists
     */
    public has(path: string): boolean {
        const normalizedPath = normalizePath(path);
        return this.data.has(normalizedPath);
    }

    /**
     * Remove data at path (alias for delete)
     */
    public remove(path: string, source: string = 'unknown'): boolean {
        return this.delete(path, source);
    }

    /**
     * Delete data at path
     */
    public delete(path: string, source: string = 'unknown'): boolean {
        const normalizedPath = normalizePath(path);
        const node = this.data.get(normalizedPath);
        
        if (!node) {
            return false;
        }

        // Check readonly
        if (node.schema?.readonly) {
            return false;
        }

        const oldValue = node.value;
        this.data.delete(normalizedPath);

        // Emit change event
        this.emitDataChangeEvent(normalizedPath, oldValue, undefined, 'delete', source);

        // Clean up subscriptions for this path
        this.cleanupPathSubscriptions(normalizedPath);

        return true;
    }

    /**
     * Subscribe to data changes
     */
    public subscribe(
        path: string,
        callback: (newValue: any, oldValue: any, changePath: string) => void,
        options: DataSubscriptionOptions = {}
    ): DataSubscription {
        const subscriptionId = `data_sub_${++this.subscriptionCounter}_${Date.now()}`;
        const normalizedPath = normalizePath(path);

        const subscription: DataSubscription = {
            id: subscriptionId,
            path: normalizedPath,
            callback,
            unsubscribe: () => this.unsubscribe(subscriptionId)
        };

        this.subscriptions.set(subscriptionId, subscription);

        // Add subscription to data node
        let node = this.data.get(normalizedPath);
        if (!node) {
            // Create node with default value if it doesn't exist
            node = {
                value: undefined,
                lastModified: Date.now(),
                modifiedBy: 'system',
                subscriptions: new Set()
            };
            this.data.set(normalizedPath, node);
        }
        node.subscriptions.add(subscriptionId);

        // Call immediately if requested
        if (options.immediate && node.value !== undefined) {
            try {
                callback(node.value, undefined, normalizedPath);
            } catch (error) {
                devError(`[SharedDataContext] Immediate callback error:`, error);
            }
        }

        return subscription;
    }

    /**
     * Unsubscribe from data changes
     */
    public unsubscribe(subscriptionId: string): void {
        const subscription = this.subscriptions.get(subscriptionId);
        if (!subscription) {
            return;
        }

        // Remove from data node
        const node = this.data.get(subscription.path);
        if (node) {
            node.subscriptions.delete(subscriptionId);
        }

        // Remove subscription
        this.subscriptions.delete(subscriptionId);
    }

    /**
     * Define schema for a data path
     */
    public defineSchema(path: string, schema: DataSchema): void {
        const normalizedPath = normalizePath(path);
        
        let node = this.data.get(normalizedPath);
        if (!node) {
            node = {
                value: schema.defaultValue,
                lastModified: Date.now(),
                modifiedBy: 'schema',
                subscriptions: new Set()
            };
            this.data.set(normalizedPath, node);
        }

        node.schema = schema;

        // Set default value if provided and no current value
        if (schema.defaultValue !== undefined && node.value === undefined) {
            node.value = schema.defaultValue;
        }
    }

    /**
     * Batch update multiple paths
     */
    public batchUpdate(
        updates: Record<string, any>,
        source: string = 'unknown'
    ): BatchUpdateResult {
        const successful: string[] = [];
        const failed: string[] = [];
        const changes: Array<{ path: string; oldValue: any; newValue: any }> = [];

        for (const [path, value] of Object.entries(updates)) {
            const normalizedPath = normalizePath(path);
            const oldValue = this.get(normalizedPath);
            
            if (this.set(path, value, source, { silent: true })) {
                successful.push(path);
                changes.push({ path: normalizedPath, oldValue, newValue: value });
            } else {
                failed.push(path);
            }
        }

        // Emit batch change event
        if (changes.length > 0) {
            this.eventStream.emit(createEvent(
                DataChangeEventType.DATA_BATCH_UPDATED,
                source,
                {
                    path: 'batch',
                    changeType: 'batch',
                    oldValue: changes.map(c => ({ [c.path]: c.oldValue })),
                    newValue: changes.map(c => ({ [c.path]: c.newValue }))
                }
            ));

            // Notify individual subscribers
            changes.forEach(change => {
                this.notifySubscribers(change.path, change.newValue, change.oldValue);
            });
        }

        return { successful, failed };
    }

    /**
     * Get data statistics
     */
    public getStatistics(): DataStatistics {
        let pathsWithSchemas = 0;
        let readonlyPaths = 0;

        this.data.forEach(node => {
            if (node.schema) pathsWithSchemas++;
            if (node.schema?.readonly) readonlyPaths++;
        });

        return {
            totalPaths: this.data.size,
            totalSubscriptions: this.subscriptions.size,
            pathsWithSchemas,
            readonlyPaths,
            memoryUsage: `${JSON.stringify(this.getAllData()).length} chars`
        };
    }

    /**
     * Clear all data
     */
    public clear(source: string = 'system'): void {
        const allPaths = Array.from(this.data.keys());
        
        allPaths.forEach(path => {
            const node = this.data.get(path);
            if (node && !node.schema?.readonly) {
                this.delete(path, source);
            }
        });
    }

    /**
     * Destroy the context
     */
    public destroy(): void {
        if (this.isDestroyed) {
            return;
        }

        this.subscriptions.clear();
        this.data.clear();
        this.isDestroyed = true;
        SharedDataContext.instance = null;
    }

    // Private helper methods

    private initializeDefaultData(): void {
        initializeDefaultSchemas((path, schema) => this.defineSchema(path, schema));
    }

    private getAllData(): Record<string, any> {
        const result: Record<string, any> = {};
        this.data.forEach((node, path) => {
            result[path] = {
                value: node.value,
                lastModified: new Date(node.lastModified).toISOString(),
                modifiedBy: node.modifiedBy,
                hasSchema: !!node.schema,
                subscriptionCount: node.subscriptions.size
            };
        });
        return result;
    }

    private emitDataChangeEvent(
        path: string,
        oldValue: any,
        newValue: any,
        changeType: 'create' | 'update' | 'delete',
        source: string
    ): void {
        const eventType = changeType === 'create' 
            ? DataChangeEventType.DATA_CREATED
            : changeType === 'update'
            ? DataChangeEventType.DATA_UPDATED
            : DataChangeEventType.DATA_DELETED;

        this.eventStream.emit(createEvent(
            eventType,
            source,
            {
                path,
                oldValue,
                newValue,
                changeType
            }
        ));
    }

    private notifySubscribers(path: string, newValue: any, oldValue: any): void {
        const node = this.data.get(path);
        if (!node) {
            return;
        }

        // Notify direct subscribers
        node.subscriptions.forEach(subscriptionId => {
            const subscription = this.subscriptions.get(subscriptionId);
            if (subscription) {
                try {
                    subscription.callback(newValue, oldValue, path);
                } catch (error) {
                    devError(`[SharedDataContext] Subscription callback error:`, error);
                }
            }
        });

        // Notify wildcard subscribers
        this.subscriptions.forEach(subscription => {
            if (subscription.path !== path && pathMatches(path, subscription.path)) {
                try {
                    subscription.callback(newValue, oldValue, path);
                } catch (error) {
                    devError(`[SharedDataContext] Pattern subscription callback error:`, error);
                }
            }
        });
    }

    private cleanupPathSubscriptions(path: string): void {
        const subscriptionsToRemove: string[] = [];
        
        this.subscriptions.forEach((subscription, id) => {
            if (subscription.path === path) {
                subscriptionsToRemove.push(id);
            }
        });

        subscriptionsToRemove.forEach(id => {
            this.subscriptions.delete(id);
        });
    }
}

/**
 * Global shared data context instance
 */
export const globalDataContext = SharedDataContext.getInstance();

/**
 * Convenience functions for common operations
 */
export const dataContext = {
    get: <T>(path: string, defaultValue?: T): T => globalDataContext.get<T>(path, defaultValue),
    set: <T>(path: string, value: T, source?: string): boolean => globalDataContext.set(path, value, source),
    watch: (path: string, callback: (newValue: any, oldValue: any) => void, options?: DataSubscriptionOptions) => 
        globalDataContext.subscribe(path, callback, options),
    update: (updates: Record<string, any>, source?: string) => globalDataContext.batchUpdate(updates, source)
};
